import { useEffect, useState } from "react";
import { Refrigerator, Plus, Pencil, Power, QrCode } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LocationSelect } from "@/components/LocationSelect";
import { QrDialog } from "@/components/QrDialog";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { fmtTemp } from "@/lib/format";

const EMPTY = { name: "", equipment_type_id: "", area_id: "", min_temperature: "", max_temperature: "", unit: "C", notes: "", required_shift_ids: [] };

export default function Equipos() {
  const { activeLocation } = useAuth();
  const [items, setItems] = useState([]);
  const [areas, setAreas] = useState([]);
  const [types, setTypes] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [qrId, setQrId] = useState(null);

  const load = () => {
    if (!activeLocation) { setItems([]); return; }
    api.get(`/equipment?location_id=${activeLocation}&include_inactive=true`).then(({ data }) => setItems(data)).catch(() => {});
    api.get(`/areas?location_id=${activeLocation}`).then(({ data }) => setAreas(data)).catch(() => {});
    api.get(`/shifts?location_id=${activeLocation}`).then(({ data }) => setShifts(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [activeLocation]);
  useEffect(() => { api.get("/equipment-types").then(({ data }) => setTypes(data)).catch(() => {}); }, []);

  const openNew = () => {
    if (!activeLocation) { toast.error("Selecciona un local primero"); return; }
    setForm(EMPTY); setEditId(null); setOpen(true);
  };
  const openEdit = (e) => { setForm({ name: e.name, equipment_type_id: e.equipment_type_id, area_id: e.area_id, min_temperature: e.min_temperature, max_temperature: e.max_temperature, unit: e.unit, notes: e.notes || "", required_shift_ids: e.required_shift_ids || [] }); setEditId(e.id); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.area_id || !form.equipment_type_id) { toast.error("Completa nombre, tipo y área"); return; }
    if (form.min_temperature === "" || form.max_temperature === "") { toast.error("Define el rango de temperatura"); return; }
    const payload = { location_id: activeLocation, area_id: form.area_id, equipment_type_id: form.equipment_type_id, name: form.name, min_temperature: Number(form.min_temperature), max_temperature: Number(form.max_temperature), unit: form.unit, notes: form.notes, required_shift_ids: form.required_shift_ids };
    try {
      if (editId) await api.put(`/equipment/${editId}`, payload);
      else await api.post("/equipment", payload);
      toast.success("Equipo guardado"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleShift = (id) => setForm((f) => ({ ...f, required_shift_ids: f.required_shift_ids.includes(id) ? f.required_shift_ids.filter((x) => x !== id) : [...f.required_shift_ids, id] }));
  const deactivate = async (e) => { await api.post(`/equipment/${e.id}/deactivate`); toast.success("Equipo desactivado"); load(); };

  return (
    <div>
      <PageHeader title="Equipos" subtitle="Equipos con rango, turnos y QR"
        right={<div className="flex items-center gap-2"><LocationSelect /><Button data-testid="new-equipment-btn" onClick={openNew} className="rounded-xl"><Plus className="mr-2 h-4 w-4" /> Nuevo</Button></div>} />
      {items.length === 0 ? (
        <EmptyState icon={Refrigerator} title="Sin equipos" subtitle={activeLocation ? "Crea tu primer equipo en este local." : "Selecciona un local."} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((e) => (
            <div key={e.id} data-testid={`equipment-${e.id}`} className={`rounded-2xl border bg-white p-4 ${e.active ? "border-slate-200" : "opacity-60"}`}>
              <div className="flex items-start justify-between">
                <div><p className="font-medium">{e.name}</p><p className="text-xs text-slate-500">{areas.find((a) => a.id === e.area_id)?.name}</p></div>
                <span className="tnum rounded-lg bg-slate-100 px-2 py-1 text-xs font-medium">{fmtTemp(e.min_temperature, e.unit)}–{fmtTemp(e.max_temperature, e.unit)}</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button data-testid={`qr-equipment-${e.id}`} size="sm" variant="outline" onClick={() => setQrId(e.id)} className="rounded-lg"><QrCode className="mr-1 h-3.5 w-3.5" /> QR</Button>
                <Button data-testid={`edit-equipment-${e.id}`} size="sm" variant="outline" onClick={() => openEdit(e)} className="rounded-lg"><Pencil className="mr-1 h-3.5 w-3.5" /> Editar</Button>
                {e.active && <Button size="sm" variant="ghost" onClick={() => deactivate(e)} className="rounded-lg text-slate-500"><Power className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Editar equipo" : "Nuevo equipo"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Nombre *</Label><Input data-testid="eq-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Tipo *</Label>
                <Select value={form.equipment_type_id} onValueChange={(v) => setForm({ ...form, equipment_type_id: v })}>
                  <SelectTrigger data-testid="eq-type" className="mt-1 h-11"><SelectValue placeholder="Tipo" /></SelectTrigger>
                  <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">Área *</Label>
                <Select value={form.area_id} onValueChange={(v) => setForm({ ...form, area_id: v })}>
                  <SelectTrigger data-testid="eq-area" className="mt-1 h-11"><SelectValue placeholder="Área" /></SelectTrigger>
                  <SelectContent>{areas.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-sm">Mín °</Label><Input data-testid="eq-min" type="number" value={form.min_temperature} onChange={(e) => setForm({ ...form, min_temperature: e.target.value })} className="mt-1 h-11 tnum" /></div>
              <div><Label className="text-sm">Máx °</Label><Input data-testid="eq-max" type="number" value={form.max_temperature} onChange={(e) => setForm({ ...form, max_temperature: e.target.value })} className="mt-1 h-11 tnum" /></div>
              <div><Label className="text-sm">Unidad</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger data-testid="eq-unit" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="C">°C</SelectItem><SelectItem value="F">°F</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm">Turnos requeridos</Label>
              <div className="mt-2 flex flex-wrap gap-3">
                {shifts.length === 0 ? <p className="text-xs text-slate-400">Crea turnos en Configuración primero.</p> :
                  shifts.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm">
                      <Checkbox data-testid={`eq-shift-${s.id}`} checked={form.required_shift_ids.includes(s.id)} onCheckedChange={() => toggleShift(s.id)} /> {s.name}
                    </label>
                  ))}
              </div>
            </div>
            <div><Label className="text-sm">Observaciones</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 h-11" /></div>
          </div>
          <DialogFooter><Button data-testid="save-equipment-btn" onClick={save} className="rounded-xl">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <QrDialog equipmentId={qrId} open={!!qrId} onClose={() => setQrId(null)} />
    </div>
  );
}
